import { PageHeader as UiPageHeader } from '@nt/ui/page-header'
import React from 'react'

import { type BreadcrumbAncestor } from '@/components/molecules/shared/breadcrumb-ancestors'
import { SERVICE_AREA_LABEL, type ServiceArea, useServiceArea } from '@/components/molecules/shared/service-area'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Text } from '@/components/ui/text'
import { cn } from '@/lib/utils'

interface PageHeaderProps {
  title: string
  description?: string
  rightContent?: React.ReactNode
  variant?: 'default' | 'welcome'
  className?: string
  level?: 1 | 2 | 3
  sticky?: boolean
  hideDescription?: boolean
  /** Natural list-page ancestor for the breadcrumb, e.g. BREADCRUMB_ANCESTORS.federalTax. Sticky mode only. */
  ancestor?: BreadcrumbAncestor
  /**
   * The service this page belongs to: shows the service dot (sticky: beside
   * the title; default: an eyebrow chip) in that service's accent. Falls back
   * to the area a `CustomerPageWrapper service` puts in force.
   */
  service?: ServiceArea
}

/** The small accent dot that marks a page as a service's own (the eyebrow dot). */
function ServiceDot({ service }: { service: ServiceArea }) {
  return (
    <span data-slot="page-header-service" data-service={service} className="inline-flex shrink-0 items-center">
      <span aria-hidden="true" className="size-2 rounded-full bg-service" />
      <span className="sr-only">{SERVICE_AREA_LABEL[service]}</span>
    </span>
  )
}

export default function PageHeader({
  title,
  description,
  rightContent,
  variant = 'default',
  className,
  level = 1,
  sticky = false,
  hideDescription = false,
  ancestor,
  service: serviceProp,
}: PageHeaderProps) {
  const areaInForce = useServiceArea()
  const service = serviceProp ?? areaInForce
  const HeadingTag = `h${level}`
  const shownDescription = description && !hideDescription ? description : undefined

  if (variant === 'welcome') {
    const welcomeSizes = {
      h1: 'type-h3 md:type-h2',
      h2: 'type-title md:type-h3',
      h3: 'type-title',
    } as const satisfies Record<string, string>
    return (
      <div className={cn('space-y-2', className)}>
        {React.createElement(
          HeadingTag,
          {
            className: cn(welcomeSizes[HeadingTag as keyof typeof welcomeSizes], 'text-foreground'),
          } as React.HTMLAttributes<HTMLHeadingElement>,
          title,
        )}
        {shownDescription && <Text>{shownDescription}</Text>}
      </div>
    )
  }

  if (sticky) {
    return (
      <div className={cn('border-b border-border', className)}>
        <div className="flex h-12 items-center gap-2">
          <SidebarTrigger className="-ml-1 hidden md:flex" />
          <Separator
            orientation="vertical"
            className="mr-2 hidden data-vertical:h-4 data-vertical:self-auto md:block"
          />
          <Breadcrumb className="min-w-0 flex-1">
            <BreadcrumbList className="flex-nowrap">
              {ancestor && (
                <>
                  <BreadcrumbItem className="hidden md:block">
                    <BreadcrumbLink href={ancestor.href}>{ancestor.label}</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator className="hidden md:block" />
                </>
              )}
              <BreadcrumbItem className="min-w-0">
                <span className="flex min-w-0 items-center gap-2">
                  {service && <ServiceDot service={service} />}
                  <span className="min-w-0 truncate">
                    <BreadcrumbPage>{title}</BreadcrumbPage>
                  </span>
                </span>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          {rightContent && <div className="ml-auto flex shrink-0 items-center gap-2">{rightContent}</div>}
        </div>
        {shownDescription && <Text className="mb-3">{shownDescription}</Text>}
      </div>
    )
  }

  // The page's title block is the design system's own (@nt/ui PageHeader): a
  // type-h3 title, a body description, actions at the end of the row, and the
  // service eyebrow chip. It has no h3 level; level 3 reads as an h2.
  return (
    <UiPageHeader
      data-service={service}
      className={cn('mb-6', className)}
      level={level === 1 ? 1 : 2}
      eyebrow={service ? SERVICE_AREA_LABEL[service] : undefined}
      title={title}
      description={shownDescription}
      actions={rightContent}
    />
  )
}
